import { useEffect, useMemo, useState } from 'react'
import { PublicKey, VersionedTransaction } from '@solana/web3.js'
import { decodeFunctionResult, encodeFunctionData, erc20Abi, isAddress } from 'viem'
import { ArrowRightLeft, CheckCircle2, RefreshCw, Wallet, X } from 'lucide-react'
import { post, sleep, short } from '../../../api/client'
import { useRoseWallet } from '../../../wallet/useRoseWallet'
import { ensureVerifiedEvm, ensureVerifiedSolana } from '../../../wallet/verify'
import type { Listing, NftDetail } from '../types'

const NATIVE_EVM = new Set([
  '0x0000000000000000000000000000000000000000',
  '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'
])
const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA')
const ASSOCIATED_TOKEN_PROGRAM_ID = new PublicKey('ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL')
const CHAINS = [
  [1, 'Ethereum'], [8453, 'Base'], [42161, 'Arbitrum'], [10, 'Optimism'],
  [56, 'BNB Chain'], [137, 'Polygon'], [43114, 'Avalanche']
] as const

function b64(bytes: Uint8Array) {
  let s = ''
  for (const byte of bytes) s += String.fromCharCode(byte)
  return btoa(s)
}
function decode64(value: string) { return Uint8Array.from(atob(value), c => c.charCodeAt(0)) }
function signedToBase64(value: any): string {
  if (!value) throw new Error('Wallet did not return a signed Solana transaction')
  if (typeof value === 'string') return value
  if (value.transaction && value.transaction !== value) return signedToBase64(value.transaction)
  if (value.signedTransaction && value.signedTransaction !== value) return signedToBase64(value.signedTransaction)
  if (typeof value.serialize === 'function') return b64(value.serialize())
  if (value instanceof Uint8Array) return b64(value)
  throw new Error('Wallet returned an unsupported Solana transaction format')
}
async function signSolana(provider: any, transaction: string) {
  const tx = VersionedTransaction.deserialize(decode64(transaction))
  if (provider?.signTransaction) return signedToBase64(await provider.signTransaction(tx))
  if (provider?.signAllTransactions) return signedToBase64((await provider.signAllTransactions([tx]))[0])
  if (provider?.request) return signedToBase64(await provider.request({ method: 'solana_signTransaction', params: { transaction } }))
  throw new Error('Connected Solana wallet cannot sign this marketplace transaction')
}
function associatedTokenAccount(mint: string, owner: string) {
  const mintKey = new PublicKey(mint)
  const ownerKey = new PublicKey(owner)
  return PublicKey.findProgramAddressSync(
    [ownerKey.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mintKey.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID
  )[0].toBase58()
}
const waitReceipt = async (provider: any, hash: string) => {
  for (let i = 0; i < 90; i += 1) {
    const receipt = await provider.request({ method: 'eth_getTransactionReceipt', params: [hash] }).catch(() => null)
    if (receipt) {
      if (receipt.status === '0x0') throw new Error('EVM transaction reverted')
      return receipt
    }
    await sleep(1500)
  }
  throw new Error('EVM transaction confirmation timed out')
}
const statusLabel = (value = '') => value.replaceAll('-', ' ').replace(/\b\w/g, c => c.toUpperCase())

export default function CrossChainCheckout({ detail, onClose, onComplete }: { detail: NftDetail; onClose: () => void; onComplete?: () => void }) {
  const listing: Listing | null | undefined = detail.listing
  const { evm, solana, evmProvider, solanaProvider, open } = useRoseWallet()
  const [form, setForm] = useState({
    source_chain: '8453',
    source_token: '0x0000000000000000000000000000000000000000',
    source_amount: '',
    delivery_wallet: solana.address || ''
  })
  const [checkout, setCheckout] = useState<any>(null)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const ready = Boolean(listing?.seller && listing?.priceLamports && detail.assetId)

  useEffect(() => {
    if (solana.address && !form.delivery_wallet) setForm(v => ({ ...v, delivery_wallet: solana.address || '' }))
  }, [solana.address])

  const canStart = useMemo(() => Boolean(ready && evm.address && solana.address && evmProvider?.request && solanaProvider && /^\d+$/.test(form.source_amount) && BigInt(form.source_amount || 0) > 0n), [ready, evm.address, solana.address, evmProvider, solanaProvider, form.source_amount])

  async function switchEvm(chainId: number) {
    const hex = `0x${chainId.toString(16)}`
    const current = await evmProvider.request({ method: 'eth_chainId' })
    if (Number.parseInt(String(current), 16) !== chainId) {
      await evmProvider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] })
    }
  }

  async function begin(event: React.FormEvent) {
    event.preventDefault()
    if (!listing || !ready) return
    if (!evm.address) { await open({ view: 'Connect', namespace: 'eip155' } as any); return }
    if (!solana.address || !solanaProvider) { await open({ view: 'Connect', namespace: 'solana' } as any); return }
    setBusy(true); setMessage('Verifying both connected wallets…')
    try {
      await ensureVerifiedEvm(evm.address, evmProvider)
      await ensureVerifiedSolana(solana.address, solanaProvider)
      const delivery = form.delivery_wallet || solana.address
      let deliveryTokenAccount: string | undefined
      if (delivery !== solana.address) {
        try { deliveryTokenAccount = associatedTokenAccount(detail.assetId, delivery) } catch { deliveryTokenAccount = undefined }
      }
      setMessage('Creating the live cross-chain checkout route…')
      const created: any = await post('/api/v1/checkout/sessions', {
        asset_id: detail.assetId,
        seller_wallet: listing.seller,
        token_account: listing.tokenAccount || undefined,
        buyer_solana_wallet: solana.address,
        delivery_solana_wallet: delivery,
        delivery_token_account: deliveryTokenAccount,
        listing_price_lamports: Number(listing.priceLamports),
        seller_expiry: Number(listing.sellerExpiry || 0),
        auction_house_address: listing.auctionHouse || undefined,
        source_chain: Number(form.source_chain),
        source_token: form.source_token,
        source_amount: form.source_amount,
        source_wallet: evm.address,
        slippage: 0.005
      })
      const tx = created.fundingTransactionRequest
      if (!tx?.to || !tx?.data || !isAddress(tx.to)) throw new Error('Cross-chain route did not return an executable EVM transaction')
      const chainId = Number(form.source_chain)
      await switchEvm(chainId)
      const quote = created?.lifi_quote?.quote || created?.lifiQuote?.quote || {}
      const approvalAddress = quote?.estimate?.approvalAddress
      if (!NATIVE_EVM.has(form.source_token.toLowerCase()) && approvalAddress) {
        if (!isAddress(form.source_token) || !isAddress(approvalAddress)) throw new Error('Cross-chain route returned an invalid token approval')
        const amount = BigInt(form.source_amount)
        const data = encodeFunctionData({ abi: erc20Abi, functionName: 'allowance', args: [evm.address as `0x${string}`, approvalAddress] })
        const raw = await evmProvider.request({ method: 'eth_call', params: [{ to: form.source_token, data }, 'latest'] })
        const allowance = decodeFunctionResult({ abi: erc20Abi, functionName: 'allowance', data: raw as `0x${string}` }) as bigint
        if (allowance < amount) {
          setMessage('Approve the exact checkout amount in your EVM wallet…')
          const approvalHash = await evmProvider.request({
            method: 'eth_sendTransaction',
            params: [{ from: evm.address, to: form.source_token, data: encodeFunctionData({ abi: erc20Abi, functionName: 'approve', args: [approvalAddress, amount] }) }]
          })
          await waitReceipt(evmProvider, approvalHash)
        }
      }
      setMessage('Confirm cross-chain funding in your EVM wallet…')
      const request: any = { from: evm.address, to: tx.to, data: tx.data, value: tx.value ? `0x${BigInt(tx.value).toString(16)}` : '0x0' }
      if (tx.gasLimit) request.gas = `0x${BigInt(tx.gasLimit).toString(16)}`
      if (tx.maxFeePerGas) request.maxFeePerGas = `0x${BigInt(tx.maxFeePerGas).toString(16)}`
      if (tx.maxPriorityFeePerGas) request.maxPriorityFeePerGas = `0x${BigInt(tx.maxPriorityFeePerGas).toString(16)}`
      if (!tx.maxFeePerGas && tx.gasPrice) request.gasPrice = `0x${BigInt(tx.gasPrice).toString(16)}`
      const hash = await evmProvider.request({ method: 'eth_sendTransaction', params: [request] })
      const funded = await post(`/api/v1/checkout/sessions/${created.id}/funding`, { source_tx_hash: hash })
      setCheckout(funded)
      setMessage(`Funding submitted · ${short(hash)}`)
    } catch (error: any) { setMessage(error?.message || 'Cross-chain checkout failed') }
    finally { setBusy(false) }
  }

  async function refresh() {
    if (!checkout?.id) return
    setBusy(true)
    try {
      const endpoint = checkout.status === 'purchase-submitted'
        ? `/api/v1/checkout/sessions/${checkout.id}/purchase/refresh`
        : `/api/v1/checkout/sessions/${checkout.id}/refresh`
      const next = await post(endpoint)
      setCheckout(next)
      setMessage(`Checkout status · ${statusLabel(next.status)}`)
      if (next.status === 'complete') onComplete?.()
    } catch (error: any) { setMessage(error?.message || 'Checkout refresh failed') }
    finally { setBusy(false) }
  }

  useEffect(() => {
    if (!checkout?.id || !['funding-submitted', 'funding-pending', 'funded', 'purchase-submitted'].includes(checkout.status)) return
    const timer = window.setInterval(() => { refresh().catch(() => {}) }, 12000)
    return () => window.clearInterval(timer)
  }, [checkout?.id, checkout?.status])

  async function purchase() {
    if (!checkout?.id || !solana.address || !solanaProvider) return
    setBusy(true)
    try {
      await ensureVerifiedSolana(solana.address, solanaProvider)
      const unsigned = checkout.purchaseTransaction || checkout.purchase_unsigned_transaction
      if (!unsigned) throw new Error('Final Solana purchase transaction is not ready')
      setMessage('Sign the final non-custodial Solana NFT purchase…')
      const signed = await signSolana(solanaProvider, unsigned)
      const next: any = await post(`/api/v1/checkout/sessions/${checkout.id}/purchase`, { signed_transaction: signed })
      setCheckout(next)
      setMessage(`NFT purchase submitted · ${short(next.purchase_signature || '')}`)
    } catch (error: any) { setMessage(error?.message || 'Final Solana purchase failed') }
    finally { setBusy(false) }
  }

  return <div className="rose-modal-backdrop" role="dialog" aria-modal="true">
    <div className="rose-modal panel checkout-modal">
      <div className="panel-head"><div><span className="eyebrow"><ArrowRightLeft size={14}/> Cross-chain NFT checkout</span><h3>{detail.name || short(detail.assetId)}</h3></div><button className="icon-btn" onClick={onClose}><X/></button></div>
      <div className="checkout-box">
        <div><span>Listing price</span><b>{Number(listing?.priceLamports || 0) / 1e9} SOL</b></div>
        <div><span>Seller</span><b>{short(listing?.seller || '')}</b></div>
        <div><span>Solana buyer</span><b>{short(solana.address || '')}</b></div>
        <div><span>EVM funding wallet</span><b>{short(evm.address || '')}</b></div>
      </div>
      {!checkout && <form className="product-form" onSubmit={begin}>
        <label className="field"><span>Source chain</span><select value={form.source_chain} onChange={e => setForm(v => ({ ...v, source_chain: e.target.value }))}>{CHAINS.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select></label>
        <label className="field"><span>Source token address</span><input value={form.source_token} onChange={e => setForm(v => ({ ...v, source_token: e.target.value.trim() }))} required/><small>Use the native-token sentinel above for the chain coin, or enter an ERC-20 contract.</small></label>
        <label className="field"><span>Source amount in smallest units</span><input inputMode="numeric" value={form.source_amount} onChange={e => setForm(v => ({ ...v, source_amount: e.target.value.replace(/\D/g, '') }))} required/></label>
        <label className="field"><span>Solana delivery wallet</span><input value={form.delivery_wallet} onChange={e => setForm(v => ({ ...v, delivery_wallet: e.target.value.trim() }))} required/></label>
        <div className="hero-actions wallet-pair-actions">
          {!evm.address && <button type="button" className="secondary" onClick={() => open({ view: 'Connect', namespace: 'eip155' } as any)}><Wallet/>Connect EVM</button>}
          {!solana.address && <button type="button" className="secondary" onClick={() => open({ view: 'Connect', namespace: 'solana' } as any)}><Wallet/>Connect Solana</button>}
          <button className="primary grow" disabled={busy || !canStart}>{busy ? <RefreshCw className="spin"/> : <ArrowRightLeft/>}Quote, approve and fund</button>
        </div>
      </form>}
      {checkout && <div className="checkout-progress">
        <div className="checkout-status-row"><span>Status</span><b>{statusLabel(checkout.status)}</b></div>
        <button className="secondary full" disabled={busy} onClick={refresh}><RefreshCw className={busy ? 'spin' : ''}/>Refresh settlement</button>
        {checkout.status === 'purchase-ready' && <button className="primary full" disabled={busy} onClick={purchase}><Wallet/>Sign final Solana purchase</button>}
        {checkout.status === 'complete' && <div className="notice"><CheckCircle2/><div><b>Checkout complete</b><span>The Solana marketplace purchase is confirmed.</span></div></div>}
      </div>}
      {message && <div className="trade-message">{message}</div>}
      <small className="modal-note">Funding is monitored by Rose. Once it settles on Solana, the connected Solana wallet signs the final NFT purchase; Rose never takes custody of your wallet keys.</small>
    </div>
  </div>
}
